import 'keen-slider/keen-slider.min.css';
import 'react-toastify/dist/ReactToastify.css';

import type { PropsWithChildren } from 'react';

import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { LicenseInfo } from '@mui/x-license';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from 'react-router/dom';
import { ToastContainer } from 'react-toastify';

import { GlobalErrorBoundary } from '@ll-platform/frontend/components/statusPages/components/GlobalErrorBoundary';
import { appConfig } from '@ll-platform/frontend/config/app.config';
import { useAmplitude } from '@ll-platform/frontend/container/analytics/useAmplitude';
import { useSentrySyncContext } from '@ll-platform/frontend/container/analytics/useSentry';
import { useSmartlook } from '@ll-platform/frontend/container/analytics/useSmartlook';
import { mainRouter } from '@ll-platform/frontend/container/router/mainRouter';
import { AnalyticsCustomIdentifyContextProvider } from '@ll-platform/frontend/core/analytics/AnalyticsCustomIdentifyContext';
import { queryClient } from '@ll-platform/frontend/core/api/reactQuerySetup';
import { ActiveAudioProvider } from '@ll-platform/frontend/core/audio/ActiveAudioProvider';
import { useUser } from '@ll-platform/frontend/core/auth/hooks/useUser';
import { FeatureFlagProvider } from '@ll-platform/frontend/core/featureFlags/FeatureFlagProvider';
import { ActiveOrganizationProvider } from '@ll-platform/frontend/core/organization/ActiveOrganizationProvider';
import { Favicon } from '@ll-platform/frontend/core/theme/components/Favicon';
import { GlobalThemeProvider } from '@ll-platform/frontend/core/theme/GlobalThemeProvider';

LicenseInfo.setLicenseKey(appConfig.REACT_APP_MUI_LICENSE_KEY);

function App() {
  return (
    <GlobalErrorBoundary>
      <Favicon />
      <QueryClientProvider client={queryClient}>
        <GlobalThemeProvider>
          <FeatureFlagProvider>
            <AnalyticsCustomIdentifyContextProvider>
              <ExternalToolsProvider>
                <ActiveOrganizationProvider>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="en"
                  >
                    <ActiveAudioProvider>
                      <RouterProvider router={mainRouter} />
                    </ActiveAudioProvider>
                    <ToastContainer />
                  </LocalizationProvider>
                  <ReactQueryDevtools initialIsOpen={false} />
                </ActiveOrganizationProvider>
              </ExternalToolsProvider>
            </AnalyticsCustomIdentifyContextProvider>
          </FeatureFlagProvider>
        </GlobalThemeProvider>
      </QueryClientProvider>
    </GlobalErrorBoundary>
  );
}

const ExternalToolsProvider = ({ children }: PropsWithChildren) => {
  const { activeUser } = useUser();

  useSentrySyncContext(activeUser);
  useAmplitude(activeUser);
  useSmartlook(activeUser);

  return children;
};

export default App;
